import OrthoProposedPlanService, {
  TOrthoProposedPlanMarkType,
  type IOrthoProposedPlanFilter,
  type TOrthoProposedPlan,
} from "@/api/models/patient/orthoProposedPlan";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import {
  createQueryKeys,
  useGenericListQuery,
  useGenericQuery,
  type TItemQueryBehaviorOptions,
} from "@/composables/shared/queries";
import { type MaybeRefOrGetter } from "vue";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

export const OrthoProposedPlanQueryKeys = createQueryKeys(
  OrthoProposedPlanService.queryKey,
  (key) => ({
    listOfPatientOrthoProposedPlans: key<IOrthoProposedPlanFilter>("list"),
    detail: key<string>("detail"),
  })
);

export const usePatientOrthoProposedPlans = (
  filters: MaybeRefOrGetter<Partial<IOrthoProposedPlanFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  return useGenericListQuery<TOrthoProposedPlan, Partial<IOrthoProposedPlanFilter>>({
    queryKey: OrthoProposedPlanQueryKeys.listOfPatientOrthoProposedPlans(),
    params: { filters, enabled },
    fetcher: (f) => OrthoProposedPlanService.getAll({}, f),
    permission: $permSubjects.PATIENT__ORTHOPROPOSEDPLAN,
  });
};

// export function usePatientOrthoProposedPlans(id: MaybeRef<string>) {
//   const refId = toRef(id);
//   const { can } = useAbility();
//   return useQuery({
//     queryKey: computed(() => ["patientOrtPlans", refId.value]),
//     queryFn: async () => {
//       const response = await OrthoProposedPlanService.getAll(refId.value);
//       return response.data;
//     },
//     enabled: computed(() => can($permActions.READ, $permSubjects.PATIENT__ORTHOPROPOSEDPLAN)),
//   });
// }
export function useOrthoProposedPlan(
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) {
  return useGenericQuery<TOrthoProposedPlan, string>({
    queryKey: OrthoProposedPlanQueryKeys.detail(),
    params,
    fetcher: (id) => OrthoProposedPlanService.get({ id }),
    permission: $permSubjects.PATIENT__ORTHOPROPOSEDPLAN,
    ...options,
  });
}

export function useOrthoProposedPlanMarkMutation(veeSetErrors?: any) {
  return useGenericMutation<TOrthoProposedPlan, { type: TOrthoProposedPlanMarkType; id: string }>({
    mutationFn: ({ type, id }) => OrthoProposedPlanService.markAs({ id, type }, {}),
    feature: "orthoProposedPlan-mark-mutation",
    params: { veeSetErrors },
    invalidates: (vars) => [
      OrthoProposedPlanQueryKeys.detail(vars.id),
      OrthoProposedPlanQueryKeys.listOfPatientOrthoProposedPlans(),
    ],
  });
}

export function useCreateOrthoProposedPlan(veeSetErrors?: any) {
  return useGenericMutation<TOrthoProposedPlan, Partial<TOrthoProposedPlan>>({
    mutationFn: (proposedPlan) =>
      OrthoProposedPlanService.create({}, proposedPlan as TOrthoProposedPlan),
    feature: "orthoProposedPlan-create",
    params: { veeSetErrors },
    invalidates: (vars) => [
      OrthoProposedPlanQueryKeys.detail(),
      OrthoProposedPlanQueryKeys.listOfPatientOrthoProposedPlans({ patient: vars.patient }),
    ],
  });
}

export function useUpdateOrthoProposedPlan(veeSetErrors?: any) {
  return useGenericMutation<TOrthoProposedPlan, Partial<TOrthoProposedPlan> & { id: string }>({
    mutationFn: (proposedPlan) =>
      OrthoProposedPlanService.update({ id: proposedPlan.id }, proposedPlan),
    feature: "orthoProposedPlan-update",
    params: { veeSetErrors },
    invalidates: (vars) => [
      OrthoProposedPlanQueryKeys.detail(vars.id),
      OrthoProposedPlanQueryKeys.listOfPatientOrthoProposedPlans({ patient: vars.patient }),
    ],
  });
}
